import classNames from 'classnames';
import type { MemoryDndItem, MemoryDragDropAction } from 'constants/memory-dnd';
import MemoryDnD from 'constants/memory-dnd';
import type { TimestampDndItem, TimestampDragDropAction } from 'constants/timestamps-dnd';
import { TIMESTAMPS_DRAG_DROP_TYPE } from 'constants/timestamps-dnd';
import React from 'react';
import { useDrop } from 'react-dnd';
import type { TimelineGutterHighlight } from 'redux/modules/timeline.types';
import { roundToNearest } from 'utils/numbers';
import styles from './MemorySidebarDropzone.module.scss';

interface Props {
  onDropMemory: (action: MemoryDragDropAction) => unknown;
  onDropTimestamp: (action: TimestampDragDropAction) => unknown;
  onDropTimestampPreview: (position: TimelineGutterHighlight) => void;
  children: React.ReactNode;
  pixelsPerMinute: number;
  offsetTopPx: number;
  snapToMinutes: number;
  style?: React.CSSProperties;
}

interface DropProps {
  canDrop: boolean;
  highlight: boolean;
}

type DropItem = MemoryDndItem | TimestampDndItem;

const MemorySidebarDropzone = (props: Props) => {
  const {
    onDropMemory,
    onDropTimestamp,
    onDropTimestampPreview,
    pixelsPerMinute,
    offsetTopPx,
    snapToMinutes,
  } = props;

  const [dropProps, dropRef] = useDrop<DropItem, void, DropProps>(
    () => ({
      accept: [MemoryDnD.TYPE, TIMESTAMPS_DRAG_DROP_TYPE],
      drop: (item, monitor) => {
        if ('memoryIds' in item) {
          onDropMemory({
            memoryIds: item.memoryIds,
            source: item.source,
            target: {
              area: MemoryDnD.AREA.MemoryInbox,
            },
          });
        } else {
          const delta = monitor.getDifferenceFromInitialOffset()!.y;
          const initialY = item.fromY!;
          const snappedY = roundToNearest(delta, snapToMinutes * pixelsPerMinute);
          const snappedAdjustment = initialY - snappedY;
          const incrementInMinutes =
            (roundToNearest(snappedAdjustment + delta, snapToMinutes * pixelsPerMinute) -
              snappedAdjustment) /
            pixelsPerMinute;
          onDropTimestamp({ timestamp: item, incrementInMinutes });
        }
      },
      canDrop: item => {
        if ('memoryIds' in item) {
          return item.source.area !== MemoryDnD.AREA.MemoryInbox;
        } else {
          return true;
        }
      },
      collect: monitor => ({
        canDrop: monitor.canDrop(),
        highlight: monitor.canDrop() && monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if ('fromY' in item && item.fromY != null && item.heightPx) {
          const delta = monitor.getDifferenceFromInitialOffset()!.y;
          const initialY = item.fromY!;
          const snappedY = roundToNearest(delta, snapToMinutes * pixelsPerMinute);
          const snappedAdjustment = initialY - snappedY;
          const increment =
            roundToNearest(snappedAdjustment + delta, snapToMinutes * pixelsPerMinute) -
            snappedAdjustment;
          const dropPreview = {
            top: increment + offsetTopPx + item.fromY,
            height: item.heightPx,
          };

          onDropTimestampPreview(dropPreview);
        }
      },
    }),
    [
      onDropMemory,
      onDropTimestamp,
      onDropTimestampPreview,
      snapToMinutes,
      offsetTopPx,
      pixelsPerMinute,
    ]
  );

  return (
    <div
      style={props.style}
      ref={dropRef}
      className={classNames(styles.container, {
        [styles.sidebarDrop]: dropProps.canDrop,
        [styles.highlightDrop]: dropProps.highlight,
      })}
    >
      {props.children}
    </div>
  );
};

export default MemorySidebarDropzone;
